import { emptyMetaBuilderSplitApi as api } from "./emptyMetaBuilderApi";

type MetaAdsAuthStatusResponse = {
  connected: boolean;
  userId?: string;
  facebookUserId?: string;
  expiresAt?: string;
  selectedPageId?: string;
  selectedAdAccountId?: string;
  selectedInstagramAccountId?: string;
};

type MetaAdsLoginResponse = {
  authURL: string;
};

type MetaAdsRefreshResponse = {
  message?: string;
};

type MetaAdsDisconnectResponse = {
  message?: string;
};

type FacebookPage = {
  id: string;
  name: string;
  category: string;
  picture?: { data?: { url?: string; height?: number; width?: number; is_silhouette?: boolean } };
};

type MetaAdsPagesResponse = {
  pages: FacebookPage[];
};

type InstagramAccount = {
  id: string;
  name?: string;
  username?: string;
  pageName?: string;
  profile_picture_url?: string;
  pageId?: string;
  [key: string]: any;
};

type MetaAdsInstagramAccountsResponse = {
  instagramAccounts?: InstagramAccount[];
  accounts?: InstagramAccount[];
  instagram_accounts?: InstagramAccount[];
};

type AdAccount = {
  id: string;
  name: string;
  account_id: string;
  account_status: number;
  currency: string;
  timezone_name: string;
};

type MetaAdsAdAccountsResponse = {
  adAccounts: AdAccount[];
};

type Pixel = {
  id: string;
  name: string;
  type?: string;
  pixelId?: string;
  capiToken?: string;
  [key: string]: any;
};

type MetaAdsPixelsResponse = {
  pixels?: Pixel[];
};

type CreateCampaignPayload = {
  campaignName: string;
  objective: string;
  adAccountId: string;
  pageId: string;
  instagramAccountId?: string;
  pixelId?: string;
  adSetName?: string;
  budget: {
    dailyBudget: number;
    budgetType: string;
  };
  targeting: {
    age_min: number;
    age_max: number;
    genders: number[];
    geo_locations: {
      countries: string[];
    };
    interests: Array<{
      id?: string;
      name: string;
    }>;
  };
  creative: {
    imageUrl: string;
    linkUrl: string;
    message?: string;
  };
  schedule?: {
    isScheduled: boolean;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
    timezone: string;
  };
  adText?: {
    headlineTop?: string;
    headlineBottom?: string;
    message?: string;
  };
  language?: string;
};

type CreateCampaignResponse = {
  success?: boolean;
  message?: string;
  campaignId?: string;
  [key: string]: any;
};

// Step-by-step campaign creation types
type CreateCampaignStep1Request = {
  campaignName: string;
  objective: string;
  adAccountId: string;
  budget: {
    type: "DAILY" | "SCHEDULED";
    amount: number;
  };
  schedule?: {
    isScheduled: boolean;
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timezone?: string;
  };
};

type CreateCampaignStep1Response = {
  msg: string;
  progress: number;
  campaign: {
    campaignId: string;
    savedCampaignId: string;
    name: string;
    objective: string;
    status: string;
    budget: {
      type?: string;
      amount?: number;
    };
  };
};

type CreateAdSetStep2Request = {
  campaignId: string;
  adAccountId: string;
  adSetName?: string;
  targeting: {
    age_min?: number;
    age_max?: number;
    genders?: number[];
    geo_locations: {
      countries?: string[];
      regions?: Array<{ key: string }>;
      cities?: Array<{ key: string; radius: string; region: string }>;
    };
    interests?: Array<{ id: string; name: string }>;
  };
  schedule?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timezone?: string;
    isScheduled: boolean;
  };
  budget?: {
    dailyBudget?: number;
  };
  pixelId?: string;
  objective: string;
  language?: string;
  dsa_beneficiary?: {
    id: string;
    type: string;
    display_name: string;
  };
  dsa_payor?: {
    id: string;
    type: string;
    display_name: string;
  };
};

type CreateAdSetStep2Response = {
  msg: string;
  progress: number;
  adSet: {
    adSetId: string;
    campaignId: string;
    name: string;
    targeting: any;
    status: string;
  };
};

type CreateAdStep3Request = {
  campaignId: string;
  adSetId: string;
  adAccountId: string;
  pageId: string;
  adName?: string;
  creative?: {
    videoId?: string;
    imageUrl?: string;
    linkUrl?: string;
    message?: string;
    headline?: string;
    description?: string;
  };
  adText?: {
    headlineTop?: string;
    headlineBottom?: string;
    message?: string;
  };
  videoId?: string;
  linkUrl?: string;       // Original Spotify/track URL
  artistName?: string;    // Artist name
  songName?: string;      // Song title
};

type CreateAdStep3Response = {
  msg: string;
  progress: number;
  ad: {
    adId: string;
    creativeId: string;
    adSetId: string;
    campaignId: string;
    name: string;
    status: string;
  };
  summary: {
    campaignId: string;
    adSetId: string;
    adId: string;
    creativeId: string;
    savedCampaignId: string;
  };
};

type CreatePixelPayload = {
  name: string;
  type?: string;
  pixelId: string;
  capiToken?: string;
  adAccountId: string;
};

type CreatePixelResponse = {
  success?: boolean;
  message?: string;
  pixelId?: string;
  [key: string]: any;
};

// Eligibility and payment method types
type EligibilityCheck = {
  accountConnected: boolean;
  isProfessional: boolean;
  accountType: string | null;
  hasAdvertisePermission: boolean;
  businessConnected: boolean;
  pageConnected: boolean;
  instagramConnected: boolean;
  pageCount: number;
  adAccountEligible: boolean;
  hasAdAccount: boolean;
  adAccountStatus: number | null;
  adAccountActive: boolean;
  isEUEEA: boolean;
  euSubscriptionStatus: string | null;
};

type EligibilityRecommendation = {
  type: string;
  priority: string;
  message: string;
  details: string;
};

type MetaAdsEligibilityResponse = {
  eligible: boolean;
  reason: string | null;
  checks: EligibilityCheck;
  recommendations: EligibilityRecommendation[];
  lastChecked: string;
};

type PaymentMethodDetails = {
  id: string;
  type: string;
  displayString: string;
  amount: number;
  currency: string;
};

type MetaAdsPaymentMethodsResponse = {
  adAccountId: string;
  hasPaymentMethod: boolean;
  paymentMethod: PaymentMethodDetails | null;
  accountStatus: number;
};

type MetaBuilderPricingResponse = {
  pricing: {
    monthlyAmount: number | null;
    yearlyAmount: number | null;
    currency: string;
  };
};

// Campaign Types
export type MetaAdsCampaign = {
  _id: string;
  userId: string;
  campaignId: string;
  campaignName: string;
  objective: string;
  status: string;
  creationStage: string;
  adAccountId: string;
  pageId?: string;
  instagramAccountId?: string;
  pixelId?: string;
  budget: {
    type: string;
    amount: number;
  };
  schedule: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timezone?: string;
    isScheduled: boolean;
  };
  adText?: {
    headlineTop?: string;
    headlineBottom?: string;
    message?: string;
  };
  language?: string;
  trackInfo?: {
    linkUrl?: string;      // Original Spotify/track URL
    smartLink?: string;    // Generated smart link URL
    artistName?: string;   // Artist name
    songName?: string;     // Song title
  };
  adSets: Array<{
    adSetId: string;
    name: string;
    status: string;
  }>;
  ads: Array<{
    adId: string;
    creativeId: string;
    name: string;
    status: string;
  }>;
  performance: {
    impressions: number;
    clicks: number;
    spend: number;
    ctr: number;
    cpc: number;
    cpm: number;
    lastUpdated?: string;
  };
  metadata: any;
  createdAt: string;
  updatedAt: string;
};

type MetaAdsCampaignsResponse = {
  campaigns: MetaAdsCampaign[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };
};

type MetaAdsCampaignsQueryParams = {
  status?: string;
  limit?: number;
  offset?: number;
};

type UpdateCampaignStatusRequest = {
  campaignId: string;
  status: "ACTIVE" | "PAUSED" | "DELETED" | "ARCHIVED";
};

type UpdateCampaignStatusResponse = {
  msg: string;
  campaign: MetaAdsCampaign;
};

type UpdateCampaignBudgetScheduleRequest = {
  campaignId: string;
  status?: "ACTIVE" | "PAUSED" | "DELETED" | "ARCHIVED";
  budget?: {
    amount: number;
  };
  schedule?: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timezone?: string;
    isScheduled?: boolean;
  };
};

type UpdateCampaignBudgetScheduleResponse = {
  msg: string;
  campaign: MetaAdsCampaign;
  metaUpdate: {
    success: boolean;
    error: string | null;
  };
};

// Campaign from Meta types
export type MetaVideoFromMeta = {
  id: string;
  title: string;
};

export type MetaCampaignFromMeta = {
  id: string;
  name: string;
  promoteType?: "track" | "playlist";
  status: string;
  objective: string;
  daily_budget: number | string; // Can be number (list endpoint) or string (single campaign endpoint)
  lifetime_budget: number | string | null;
  budget_remaining: number | string | null;
  start_time: string;
  stop_time: string | null;
  created_time: string;
  updated_time: string;
  account_id: string;
  effective_status: string;
  buying_type: string;
  bid_strategy?: string;
  linkUrls?: string[];
  videos?: MetaVideoFromMeta[];
  trackInfo?: {
    linkUrl?: string;
    smartLink?: string;
    artistName?: string;
    songName?: string;
  };
  performance?: {
    dateRange: string;
    reach: number;
    impressions: number;
    clicks: number;
    spend: number;
    conversions: number;
  };
};

export type MetaAccountFromMeta = {
  id: string;
  name: string;
  account_status: number;
  currency: string;
  timezone_name: string;
  business_name: string;
  spend_cap: string;
};

export type MetaAdSetFromMeta = {
  id: string;
  name: string;
  status: string;
  targeting: {
    age_max: number;
    age_min: number;
    age_range: number[];
    geo_locations: {
      countries: string[];
      location_types: string[];
    };
    interests: Array<{
      id: string;
      name: string;
    }>;
    flexible_spec?: Array<{
      interests: Array<{
        id: string;
        name: string;
      }>;
    }>;
    targeting_automation?: {
      advantage_audience: number;
    };
    publisher_platforms: string[];
    facebook_positions?: string[];
    instagram_positions?: string[];
  };
};

export type MetaAdFromMeta = {
  id: string;
  name: string;
  creative: {
    object_story_spec: {
      page_id: string;
      video_data: {
        video_id: string;
        title: string;
        message: string;
        call_to_action: {
          type: string;
          value: {
            link: string;
          };
        };
        image_url: string;
        image_hash: string;
      };
    };
    id: string;
  };
};

type MetaAdsCampaignsFromMetaResponse = {
  campaigns: MetaCampaignFromMeta[];
  total: number;
  fetchedAt: string;
};

type MetaAdsCampaignsFromMetaQueryParams = {
  adAccountId?: string;
  status?: string;
  limit?: number;
  dateRange?: string;
};

type MetaAdsCampaignFromMetaResponse = {
  campaign: MetaCampaignFromMeta;
  account: MetaAccountFromMeta;
  countries: string[];
  linkUrls: string[];
  adSets: MetaAdSetFromMeta[];
  ads: MetaAdFromMeta[];
  insights: {
    impressions: string;
    clicks: string;
    spend: string;
    ctr: string;
    cpc: string;
    cpm: string;
    reach: string;
    actions: string;
    cost_per_action_type: string;
    frequency: string;
    action_values: string;
  } | null;
  fetchedAt: string;
  /** Instagram account ID from the campaign's creative */
  instagramAccountId?: string | null;
  /** Original Spotify/track URL from our DB (for edit mode; avoids showing smart link in music dropdown) */
  originalSpotifyUrl?: string | null;
  originalArtistName?: string | null;
  originalSongName?: string | null;
  /** Complete metadata from database */
  metadata?: {
    promoteType?: "track" | "playlist";
    genres?: string[];
    audioAsset?: {
      key: string;
      url: string;
      streamUrl?: string;
      fileName: string;
      contentType?: string;
    } | null;
    landingAsset?: {
      key: string;
      url: string;
      streamUrl?: string;
      fileName: string;
      contentType?: string;
    } | null;
    spotifyUrl?: string;
    artistName?: string;
    title?: string;
    /** Facebook page name */
    pageName?: string;
    /** Instagram account name */
    instagramName?: string;
    /** Facebook page image URL */
    pageImageUrl?: string;
    /** Instagram profile image URL */
    instagramProfileImageUrl?: string;
    [key: string]: any;
  };
};

type DuplicateCampaignResponse = {
  msg: string;
  originalCampaignId: string;
  newCampaignId: string;
  newCampaignName: string;
  adSetsCopied: number;
};

type VideoData = {
  id: string;
  video_id: string;
  title?: string;
  description?: string;
  thumbnail_url?: string;
  status?: string;
  created_time?: string;
  updated_time?: string;
  [key: string]: any;
};

type GetVideosByIdsResponse = {
  videos: VideoData[];
  total: number;
  fetchedAt: string;
};

type GetVideosByIdsRequest = {
  videoIds: string[];
};

// Draft Campaign Types
type DraftCampaign = {
  _id: string;
  userId: string;
  campaignName: string;
  objective: string;
  status: string;
  creationStage: string;
  adAccountId?: string;
  pageId?: string;
  instagramAccountId?: string;
  pixelId?: string;
  budget: {
    type: string;
    amount: number;
  };
  targeting?: {
    countries?: string[];
    interests?: any[];
    age_min?: number;
    age_max?: number;
    genders?: number[];
  };
  schedule: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    timezone?: string;
    isScheduled: boolean;
  };
  adText?: {
    headlineTop?: string;
    headlineBottom?: string;
    message?: string;
  };
  s3Videos?: Array<{
    key: string;
    fileName: string;
    uploadedAt: string;
  }>;
  metadata: {
    promoteType?: "track" | "playlist";
    genres?: string[];
    audioAsset?: {
      key: string;
      url: string;
      fileName: string;
      contentType?: string;
    } | null;
    landingAsset?: {
      key: string;
      url: string;
      fileName: string;
      contentType?: string;
    } | null;
    spotifyUrl?: string;
    artistName?: string;
    title?: string;
    draftData?: {
      campaignStartDate?: string;
      campaignEndDate?: string;
      originalData?: any;
    };
  };
  createdAt: string;
  updatedAt: string;
};

type GetDraftCampaignsResponse = {
  success: boolean;
  count: number;
  drafts: DraftCampaign[];
};

type GetDraftCampaignsQueryParams = {
  adAccountId?: string;
};

type GetDraftCampaignByIdResponse = {
  success: boolean;
  draft: DraftCampaign;
};

type DeleteDraftResponse = {
  success: boolean;
  message: string;
};

type SaveDraftCampaignRequest = {
  campaignData: any;
  videos?: File[];
};

type SaveDraftCampaignResponse = {
  success: boolean;
  draftId: string;
  message: string;
};

type UpdateDraftCampaignRequest = {
  draftId: string;
  campaignData: any;
  videos?: File[];
};

type DeleteCampaignResponse = {
  msg: string;
  campaignId: string;
  deletedAt: string;
};

type DeleteAdSetResponse = {
  msg: string;
  adSetId: string;
  campaignId: string;
  deletedAt: string;
};

type DeleteAdResponse = {
  msg: string;
  adId: string;
  creativeId: string | null;
  campaignId: string;
  deletedAt: string;
};

// Campaign by smart link types
type CampaignsByUserResponse = {
  campaigns: CampaignData[];
  total: number;
};

type CampaignData = {
  campaignId: string;
  campaignName: string;
  pixelId?: string;
  trackInfo?: {
    linkUrl?: string;
    smartLink?: string;
    artistName?: string;
    songName?: string;
  };
  status: string;
};

type CampaignBySmartLinkResponse = {
  campaignId: string;
  campaignName: string;
  pixelId?: string;
  trackInfo?: {
    linkUrl?: string;
    smartLink?: string;
    artistName?: string;
    songName?: string;
  };
  status: string;
};

type TrackPlayClickRequest = {
  campaignId: string;
  action: "visit" | "spotify";
  testEventCode?: string;
};

type TrackPlayClickResponse = {
  msg: string;
  eventId?: string;
};

// Billing Details Types
type BillingInvoice = {
  id: string;
  date: string;
  status: string;
  amount: string;
  pdfUrl: string;
};

type BillingDetailsResponse = {
  billingName: string;
  email: string;
  billingAddress: string;
  invoices: BillingInvoice[];
};

export type { DraftCampaign, GetDraftCampaignsResponse, GetDraftCampaignsQueryParams, GetDraftCampaignByIdResponse, BillingInvoice, BillingDetailsResponse };

const injectedRtkApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMetaBuilderAuthStatus: builder.query<MetaAdsAuthStatusResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/auth/status`,
        };
      },
      providesTags: ['MetaAdsAuth'],
    }),
    saveMetaBuilderSelections: builder.mutation<
      { msg: string; selections: { pageId?: string; adAccountId?: string; instagramAccountId?: string } },
      { pageId?: string | null; adAccountId?: string | null; instagramAccountId?: string | null }
    >({
      query: (body) => {
        return {
          url: `/api/meta-builder/save-selections`,
          method: "POST",
          body: {
            pageId: body.pageId ?? undefined,
            adAccountId: body.adAccountId ?? undefined,
            instagramAccountId: body.instagramAccountId ?? undefined,
          },
        };
      },
      invalidatesTags: ['MetaAdsAuth'],
    }),
    getSubscription: builder.query<
      {
        subscription: {
          startDate?: string;
          endDate?: string;
          status: string;
          type?: "monthly" | "yearly";
          packageName?: string;
          stripeCustomerId?: string;
          cancelAtPeriodEnd?: boolean;
        } | null;
        expired?: boolean;
      },
      string
    >({
      query: (userId) => {
        return {
          url: `/api/subscription`,
          params: { userId },
        };
      },
      providesTags: ['MetaAdsAuth'],
    }),
    getMetaBuilderPricing: builder.query<MetaBuilderPricingResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/pricing`,
        };
      },
      providesTags: ['MetaBuilderPricing'],
    }),
    createSubscriptionCheckout: builder.mutation<
      { url: string; sessionId?: string },
      { userId: string; packageType: "monthly" | "yearly"; packageName?: string; successUrl?: string; cancelUrl?: string }
    >({
      query: (body) => {
        return {
          url: `/api/subscription/create-checkout`,
          method: "POST",
          body: {
            userId: body.userId,
            packageType: body.packageType,
            packageName: body.packageName ?? "Meta Ads Builder",
            successUrl: body.successUrl,
            cancelUrl: body.cancelUrl,
          },
        };
      },
    }),
    changeSubscriptionPlan: builder.mutation<
      { success: boolean; message: string; subscription?: { startDate: string; endDate: string; status: string; type: "monthly" | "yearly" } },
      { userId: string; newPackageType: "monthly" | "yearly"; packageName?: string }
    >({
      query: (body) => {
        return {
          url: `/api/subscription/change-plan`,
          method: "POST",
          body: {
            userId: body.userId,
            newPackageType: body.newPackageType,
            packageName: body.packageName ?? "Meta Ads Builder",
          },
        };
      },
      invalidatesTags: ['MetaAdsAuth'],
    }),
    cancelSubscription: builder.mutation<
      { success: boolean; message: string; cancelAtPeriodEnd: boolean },
      { userId: string; cancelAtPeriodEnd?: boolean; deleted?: boolean }
    >({
      query: (body) => {
        return {
          url: `/api/subscription/cancel`,
          method: "POST",
          body: {
            userId: body.userId,
            cancelAtPeriodEnd: body.cancelAtPeriodEnd ?? true,
            deleted: body.deleted ?? false,
          },
        };
      },
      invalidatesTags: ['MetaAdsAuth'],
    }),
    getBillingDetails: builder.query<BillingDetailsResponse, string>({
      query: (userId) => {
        return {
          url: `/api/subscription/billing-details`,
          params: { userId },
        };
      },
      providesTags: ['BillingDetails'],
    }),
    updateBillingDetails: builder.mutation<
      { success: boolean; message: string; billingName: string; email: string },
      { userId: string; billingName: string; email?: string }
    >({
      query: (body) => {
        return {
          url: `/api/subscription/billing-details`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ['BillingDetails'],
    }),
    createPortalSession: builder.mutation<
      { url: string },
      { userId: string; returnUrl?: string }
    >({
      query: (body) => {
        return {
          url: `/api/subscription/create-portal-session`,
          method: "POST",
          body,
        };
      },
    }),
    metaBuilderLogin: builder.mutation<MetaAdsLoginResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/auth/login`,
          method: "GET",
        };
      },
      invalidatesTags: ['MetaAdsAuth', 'MetaAdsPages', 'MetaAdsInstagramAccounts', 'MetaAdsAdAccounts', 'MetaAdsPixels', 'MetaAdsSavedPixels'],
    }),
    metaBuilderRefresh: builder.mutation<MetaAdsRefreshResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/auth/refresh`,
          method: "POST",
        };
      },
      invalidatesTags: ['MetaAdsAuth', 'MetaAdsPages', 'MetaAdsInstagramAccounts', 'MetaAdsAdAccounts', 'MetaAdsPixels', 'MetaAdsSavedPixels'],
    }),
    metaBuilderDisconnect: builder.mutation<MetaAdsDisconnectResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/auth/disconnect`,
          method: "POST",
          headers: {
            Authorization:
              "Bearer BQCa7ZOzF6-33Y2zDwwLZJtSdU9075ytovMDuYdSiONHUYjOY2tbzhXlnxWwpTl7NWfWioFHc6KL6xveMX6_14nWgs0v5Myv4UigeqJqJXjl-Z75DOHa_-8ANeW2MOcqpxfcRy_Ex9EflJfcXJ7GvjXUSXA2o5pv9BH8-vu7383GUAJDUYYvmUZ6yl1BilH5n_MhHbD1GX6VJ5zaVkqKybftEr2RFGi0dq32eelT1MAWyuq5H3DByB-MfRuEyMXG-htSuCo23fCWiSTrZEogHJK2whC2yNzcxt760nsEffrPdowM3Di-a-weWOkSvsEx26BXQGVyQklt2AFo5Z0k8OajWgH5jtJaeHay",
          },
        };
      },
      invalidatesTags: ['MetaAdsAuth', 'MetaAdsPages', 'MetaAdsInstagramAccounts', 'MetaAdsAdAccounts', 'MetaAdsPixels', 'MetaAdsSavedPixels'],
    }),
    getMetaBuilderPages: builder.query<MetaAdsPagesResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/pages`,
        };
      },
      providesTags: ['MetaAdsPages'],
    }),
    getMetaBuilderInstagramAccounts: builder.query<MetaAdsInstagramAccountsResponse, { pageId?: string }>({
      query: ({ pageId } = {}) => {
        return {
          url: `/api/meta-builder/instagram-accounts`,
          params: pageId ? { pageId } : undefined,
        };
      },
      providesTags: ['MetaAdsInstagramAccounts'],
    }),
    getMetaBuilderAdAccounts: builder.query<MetaAdsAdAccountsResponse, { pageId: string }>({
      query: ({ pageId }) => {
        return {
          url: `/api/meta-builder/ad-accounts`,
          params: {
            pageId,
          },
        };
      },
      providesTags: ['MetaAdsAdAccounts'],
    }),
    getMetaBuilderPixels: builder.query<MetaAdsPixelsResponse, { adAccountId: string; facebookUserId?: string }>({
      query: ({ adAccountId, facebookUserId }) => {
        return {
          url: `/api/meta-builder/pixels`,
          params: {
            adAccountId,
            ...(facebookUserId && { facebookUserId }),
          },
        };
      },
      providesTags: ['MetaAdsPixels'],
    }),
    getMetaBuilderSavedPixels: builder.query<MetaAdsPixelsResponse, { facebookUserId?: string }>({
      query: ({ facebookUserId }) => {
        return {
          url: `/api/meta-builder/pixels/saved`,
          params: facebookUserId ? { facebookUserId } : undefined,
        };
      },
      providesTags: ['MetaAdsSavedPixels'],
    }),
    createMetaBuilderPixel: builder.mutation<CreatePixelResponse, CreatePixelPayload>({
      query: (body) => {
        return {
          url: `/api/meta-builder/pixels`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ['MetaAdsSavedPixels'],
    }),
    updateMetaBuilderPixel: builder.mutation<{ msg: string; pixel?: any }, { pixelId: string; name?: string; type?: string; capiToken?: string; adAccountId?: string }>({
      query: ({ pixelId, ...body }) => {
        return {
          url: `/api/meta-builder/pixels/${pixelId}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ['MetaAdsSavedPixels'],
    }),
    deleteMetaBuilderPixel: builder.mutation<{ msg: string }, { pixelId: string }>({
      query: ({ pixelId }) => {
        return {
          url: `/api/meta-builder/pixels/${pixelId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['MetaAdsSavedPixels'],
    }),
    createMetaBuilderCampaign: builder.mutation<CreateCampaignResponse, CreateCampaignPayload>({
      query: (body) => {
        return {
          url: `/api/meta-builder/campaigns`,
          method: "POST",
          body,
        };
      },
    }),
    // Step-by-step campaign creation APIs
    createMetaBuilderCampaignStep1: builder.mutation<CreateCampaignStep1Response, CreateCampaignStep1Request>({
      query: (body) => {
        return {
          url: `/api/meta-builder/campaigns/create`,
          method: "POST",
          body,
        };
      },
    }),
    createMetaBuilderAdSetStep2: builder.mutation<CreateAdSetStep2Response, CreateAdSetStep2Request>({
      query: (body) => {
        return {
          url: `/api/meta-builder/adsets/create`,
          method: "POST",
          body,
        };
      },
    }),
    createMetaBuilderAdStep3: builder.mutation<CreateAdStep3Response, FormData>({
      query: (body) => {
        return {
          url: `/api/meta-builder/ads/create`,
          method: "POST",
          body,
        };
      },
    }),
    getMetaBuilderEligibility: builder.query<MetaAdsEligibilityResponse, void>({
      query: () => {
        return {
          url: `/api/meta-builder/eligibility`,
        };
      },
    }),
    getMetaBuilderPaymentMethods: builder.query<MetaAdsPaymentMethodsResponse, { adAccountId: string }>({
      query: ({ adAccountId }) => {
        return {
          url: `/api/meta-builder/payment-methods/${adAccountId}`,
        };
      },
    }),
    getMetaBuilderCampaigns: builder.query<MetaAdsCampaignsResponse, MetaAdsCampaignsQueryParams | void>({
      query: (params = {}) => {
        return {
          url: `/api/meta-builder/campaigns`,
          params: {
            status: params?.status,
            limit: params?.limit,
            offset: params?.offset,
          },
        };
      },
      providesTags: ['MetaAdsCampaigns'],
    }),
    updateMetaBuilderCampaignStatus: builder.mutation<UpdateCampaignStatusResponse, UpdateCampaignStatusRequest>({
      query: ({ campaignId, status }) => {
        return {
          url: `/api/meta-builder/campaigns/${campaignId}/status`,
          method: "PUT",
          body: { status },
        };
      },
      invalidatesTags: ['MetaAdsCampaigns'],
    }),
    updateMetaBuilderCampaignBudgetSchedule: builder.mutation<UpdateCampaignBudgetScheduleResponse, UpdateCampaignBudgetScheduleRequest>({
      query: ({ campaignId, ...body }) => {
        return {
          url: `/api/meta-builder/campaigns/${campaignId}/update`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ['MetaAdsCampaigns', 'MetaAdsCampaignsFromMeta'],
    }),
    // New endpoints to fetch campaigns directly from Meta
    getMetaBuilderCampaignsFromMeta: builder.query<MetaAdsCampaignsFromMetaResponse, MetaAdsCampaignsFromMetaQueryParams | void>({
      query: (params = {}) => {
        return {
          url: `/api/meta-builder/campaigns/from-meta`,
          params: {
            adAccountId: params?.adAccountId,
            status: params?.status,
            limit: params?.limit,
            dateRange: params?.dateRange,
          },
        };
      },
      providesTags: ['MetaAdsCampaignsFromMeta'],
    }),
    getMetaBuilderCampaignFromMeta: builder.query<MetaAdsCampaignFromMetaResponse, { campaignId: string }>({
      query: ({ campaignId }) => {
        return {
          url: `/api/meta-builder/campaigns/${campaignId}/from-meta`,
        };
      },
      providesTags: ['MetaAdsCampaignFromMeta'],
    }),
    duplicateMetaBuilderCampaign: builder.mutation<DuplicateCampaignResponse, { campaignId: string }>({
      query: ({ campaignId }) => {
        return {
          url: `/api/meta-builder/campaigns/${campaignId}/duplicate`,
          method: "POST",
        };
      },
      invalidatesTags: ['MetaAdsCampaignsFromMeta'],
    }),
    getVideosByIds: builder.query<GetVideosByIdsResponse, GetVideosByIdsRequest>({
      query: ({ videoIds }) => {
        return {
          url: `/api/meta-builder/videos/by-ids`,
          method: "POST",
          body: { videoIds },
        };
      },
    }),
    getDraftCampaigns: builder.query<GetDraftCampaignsResponse, GetDraftCampaignsQueryParams | void>({
      query: (params = {}) => {
        return {
          url: `/api/meta-builder/campaigns/drafts`,
          params: {
            adAccountId: params?.adAccountId,
          },
        };
      },
      providesTags: ['DraftCampaigns'],
    }),
    getDraftCampaignById: builder.query<GetDraftCampaignByIdResponse, string>({
      query: (draftId) => {
        return {
          url: `/api/meta-builder/campaigns/draft/${draftId}`,
        };
      },
      providesTags: (result, error, draftId) => [{ type: 'DraftCampaigns', id: draftId }],
    }),
    deleteDraftCampaign: builder.mutation<DeleteDraftResponse, string>({
      query: (draftId) => {
        return {
          url: `/api/meta-builder/campaigns/draft/${draftId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['DraftCampaigns'],
    }),
    saveDraftCampaign: builder.mutation<SaveDraftCampaignResponse, FormData>({
      query: (formData) => {
        return {
          url: `/api/meta-builder/campaigns/draft`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ['DraftCampaigns'],
    }),
    updateDraftCampaign: builder.mutation<SaveDraftCampaignResponse, { draftId: string; formData: FormData }>({
      query: ({ draftId, formData }) => {
        return {
          url: `/api/meta-builder/campaigns/draft/${draftId}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ['DraftCampaigns'],
    }),
    // Delete campaign from Meta and database
    deleteMetaBuilderCampaign: builder.mutation<DeleteCampaignResponse, string>({
      query: (campaignId) => {
        return {
          url: `/api/meta-builder/campaigns/${campaignId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['MetaAdsCampaignsFromMeta'],
    }),
    // Delete adset from Meta and database
    deleteMetaBuilderAdSet: builder.mutation<DeleteAdSetResponse, string>({
      query: (adSetId) => {
        return {
          url: `/api/meta-builder/adsets/${adSetId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['MetaAdsCampaignsFromMeta'],
    }),
    // Delete ad and creative from Meta and database
    deleteMetaBuilderAd: builder.mutation<DeleteAdResponse, string>({
      query: (adId) => {
        return {
          url: `/api/meta-builder/ads/${adId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ['MetaAdsCampaignsFromMeta'],
    }),
    // Get campaigns by user (for referral page)
    getCampaignsByUser: builder.query<CampaignsByUserResponse, string>({
      query: (userId) => {
        return {
          url: `/api/meta-builder/campaigns/by-user/${userId}`,
        };
      },
    }),
    // Get campaign by smart link (for referral page)
    getCampaignBySmartLink: builder.query<CampaignBySmartLinkResponse, string>({
      query: (campaignId) => {
        return {
          url: `/api/meta-builder/campaign-by-smartlink/${campaignId}`,
        };
      },
    }),
    // Track play button click with CAPI
    trackPlayClick: builder.mutation<TrackPlayClickResponse, TrackPlayClickRequest>({
      query: ({ campaignId, action, testEventCode }) => {
        return {
          url: `/api/meta-builder/track-play-click`,
          method: "POST",
          body: { campaignId, action, ...(testEventCode && { testEventCode }) },
        };
      },
    }),
  }),
});

export { injectedRtkApi as metaBuilderApi };

export const { useGetMetaBuilderAuthStatusQuery, useSaveMetaBuilderSelectionsMutation, useGetSubscriptionQuery, useGetMetaBuilderPricingQuery, useCreateSubscriptionCheckoutMutation, useChangeSubscriptionPlanMutation, useCancelSubscriptionMutation, useGetBillingDetailsQuery, useUpdateBillingDetailsMutation, useCreatePortalSessionMutation, useMetaBuilderLoginMutation, useMetaBuilderRefreshMutation, useMetaBuilderDisconnectMutation, useGetMetaBuilderPagesQuery, useGetMetaBuilderInstagramAccountsQuery, useGetMetaBuilderAdAccountsQuery, useGetMetaBuilderPixelsQuery, useGetMetaBuilderSavedPixelsQuery, useCreateMetaBuilderPixelMutation, useUpdateMetaBuilderPixelMutation, useDeleteMetaBuilderPixelMutation, useCreateMetaBuilderCampaignMutation, useCreateMetaBuilderCampaignStep1Mutation, useCreateMetaBuilderAdSetStep2Mutation, useCreateMetaBuilderAdStep3Mutation, useGetMetaBuilderEligibilityQuery, useGetMetaBuilderPaymentMethodsQuery, useGetMetaBuilderCampaignsQuery, useUpdateMetaBuilderCampaignStatusMutation, useUpdateMetaBuilderCampaignBudgetScheduleMutation, useGetMetaBuilderCampaignsFromMetaQuery, useGetMetaBuilderCampaignFromMetaQuery, useDuplicateMetaBuilderCampaignMutation, useGetVideosByIdsQuery, useLazyGetVideosByIdsQuery, useGetDraftCampaignsQuery, useGetDraftCampaignByIdQuery, useDeleteDraftCampaignMutation, useSaveDraftCampaignMutation, useUpdateDraftCampaignMutation, useDeleteMetaBuilderCampaignMutation, useDeleteMetaBuilderAdSetMutation, useDeleteMetaBuilderAdMutation, useGetCampaignsByUserQuery, useGetCampaignBySmartLinkQuery, useTrackPlayClickMutation } = injectedRtkApi;
